import { Search } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import {
	Box,
	IconButton,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { flexRender, useTable } from "@tanstack/react-table";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetSettingsJSONQuery } from "../../../features/auth/authApiSlice";
import { cargoTableFeatures } from "../../../tanstack";
import { getDayAndMonthFromDate } from "../../../utils/parseDate";
import { parseTime } from "../../../utils/parseTime";
import TableSkeleton from "../../skeletons/TableSkeleton";
import { cellsStyles, headerStyles } from "../CargosTable/utils";

const GRID_INDEXES_FOR_FLEX = [0, 2, 4, 5, 6, 7, 9];

export const PublicListingTable = ({ isLoading, data }) => {
	const { t } = useTranslation();
	const { data: settings, isLoading: isLoadingSettings } =
		useGetSettingsJSONQuery();
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: 50, // Number of rows per page
	});
	const [globalFilter, setGlobalFilter] = useState("");

	const columns = useMemo(
		() => [
			{
				id: "loadingDate",
				header: t("date"),
				accessorFn: (row) => {
					const loadingDateType = row.loadingDateType;
					const date =
						loadingDateType === "day"
							? getDayAndMonthFromDate(row.loadingDate)
							: JSON.parse(row.loadingDateRange)
									.map((date) => getDayAndMonthFromDate(date))
									.join(" - ");

					const startTime = parseTime(row.loadingTimeStart);
					const endTime = row.loadingTimeEnd && parseTime(row.loadingTimeEnd);
					const time = [startTime, endTime].filter(Boolean);

					return `${date} ${time.join(" - ")}`;
				},
				cell: ({ row }) => {
					const loadingDateType = row.original.loadingDateType;
					const date =
						loadingDateType === "day"
							? getDayAndMonthFromDate(row.original.loadingDate)
							: JSON.parse(row.original.loadingDateRange)
									.map((date) => getDayAndMonthFromDate(date))
									.join(" - ");

					const startTime = parseTime(row.original.loadingTimeStart);
					const endTime =
						row.original.loadingTimeEnd &&
						parseTime(row.original.loadingTimeEnd);
					const time = [startTime, endTime].filter(Boolean);

					return (
						<Box
							sx={{
								lineHeight: 1,
								position: "relative",
								"@media screen and (max-width: 960px)": {
									display: "flex",
									gap: "10px",
								},
							}}
						>
							<Box>{date}</Box>
							<Box sx={{ color: "rgb(201, 199, 199)" }}>{time.join(" - ")}</Box>
						</Box>
					);
				},
			},
			{
				header: t("load"),
				accessorKey: "loadingAddress",
				accessorFn: (row) => row.loadingAddress?.title,
				cell: ({ row }) => {
					const value = row.original.loadingAddress;
					return (
						<Box
							sx={{
								display: "flex",
								flexWrap: "nowrap",
								alignItems: "center",
								gap: "4px",
							}}
						>
							<span
								className={`fi fi-${value.address.countryCode.toLowerCase()} fis`}
								style={{
									marginRight: "4px",
									borderRadius: "50%",
									height: "19.2px",
									width: "19.2px",
									flex: "0 0 19.2px",
								}}
							></span>
							<span style={{ whiteSpace: "nowrap" }}>
								{value.address.countryCode} {value.address.postalCode}{" "}
								{value.address.city}
							</span>
						</Box>
					);
				},
			},
			{
				id: "unloadingDate",
				header: t("date"),
				accessorFn: (row) => {
					const unloadingDateType = row.unloadingDateType;
					const date =
						unloadingDateType === "day"
							? getDayAndMonthFromDate(row.unloadingDate)
							: JSON.parse(row.unloadingDateRange)
									.map((date) => getDayAndMonthFromDate(date))
									.join(" - ");

					const startTime = parseTime(row.unloadingTimeStart);
					const endTime =
						row.unloadingTimeEnd && parseTime(row.unloadingTimeEnd);
					const time = [startTime, endTime].filter(Boolean);

					return `${date} ${time.join(" - ")}`;
				},
				cell: ({ row }) => {
					const unloadingDateType = row.original.unloadingDateType;
					const date =
						unloadingDateType === "day"
							? getDayAndMonthFromDate(row.original.unloadingDate)
							: JSON.parse(row.original.unloadingDateRange)
									.map((date) => getDayAndMonthFromDate(date))
									.join(" - ");

					const startTime = parseTime(row.original.unloadingTimeStart);
					const endTime =
						row.original.unloadingTimeEnd &&
						parseTime(row.original.unloadingTimeEnd);
					const time = [startTime, endTime].filter(Boolean);

					return (
						<Box
							sx={{
								lineHeight: 1,
								position: "relative",
								"@media screen and (max-width: 960px)": {
									display: "flex",
									gap: "10px",
								},
							}}
						>
							<Box>{date}</Box>
							<Box sx={{ color: "rgb(201, 199, 199)" }}>{time.join(" - ")}</Box>
						</Box>
					);
				},
			},
			{
				header: t("unload"),
				accessorKey: "unloadingAddress",
				accessorFn: (row) => row.unloadingAddress?.title,
				cell: ({ row }) => {
					const value = row.original.unloadingAddress;
					return (
						<Box
							sx={{
								display: "flex",
								flexWrap: "nowrap",
								alignItems: "center",
								gap: "4px",
							}}
						>
							<span
								className={`fi fi-${value.address.countryCode.toLowerCase()} fis`}
								style={{
									marginRight: "4px",
									borderRadius: "50%",
									height: "19.2px",
									width: "19.2px",
									flex: "0 0 19.2px",
								}}
							></span>
							<span style={{ whiteSpace: "nowrap" }}>
								{value.address.countryCode} {value.address.postalCode}{" "}
								{value.address.city}
							</span>
						</Box>
					);
				},
			},
			{
				header: "M",
				accessorKey: "length",
			},
			{
				header: "T",
				accessorKey: "weight",
			},
			{
				id: "isFtl",
				header: () => <span>F&nbsp;/&nbsp;L</span>,
				accessorFn: (row) => {
					const value = row.isFtl;
					return value === 1 ? "FTL" : "LTL";
				},
				cell: ({ row }) => {
					const value = row.original.isFtl;
					return value === 1 ? "FTL" : "LTL";
				},
			},
			{
				header: t("vehicle"),
				accessorFn: (row) => {
					const value = row.vehicleBodyId;
					const preparedString = value
						.map((val) => settings.vehicleOptions.vehicleBodyTypesOptions[val])
						.join(", ");

					return preparedString;
				},
				cell: ({ row }) => {
					const value = row.original.vehicleBodyId;
					const preparedString = value
						.map((val) => settings.vehicleOptions.vehicleBodyTypesOptions[val])
						.join(", ");

					return preparedString;
				},
			},
			{
				header: t("price"),
				accessorFn: (row) => {
					const value = row.price;
					return value ? `${value} €` : "-";
				},
				cell: ({ row }) => {
					const value = row.original.price;

					return value ? <span>{value}&nbsp;€</span> : "-";
				},
			},
		],
		[settings],
	);

	const table = useTable({
		features: cargoTableFeatures,
		data,
		columns,
		getRowId: (row) => row.id,
		onPaginationChange: setPagination,
		onGlobalFilterChange: setGlobalFilter,
		state: {
			pagination,
			globalFilter,
		},
	});

	if (isLoading || isLoadingSettings) return <TableSkeleton />;

	return (
		<>
			<Box sx={{ pt: "20px", maxWidth: "80%" }}>
				<TextField
					variant="standard"
					value={globalFilter}
					onChange={(e) => setGlobalFilter(e.target.value)}
					sx={{ width: "100%", marginBottom: "10px" }}
					placeholder={t("search_dot_dot_dot")}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<Search />
								</InputAdornment>
							),
						},
					}}
				/>
			</Box>
			<Box sx={{ pt: "10px", overflow: "scroll" }}>
				<TableView table={table} columns={columns} />
				<GridView table={table} />
			</Box>
			<TablePagination
				component="div"
				count={data?.length} // Total number of rows in the table
				rowsPerPageOptions={[50, 100]} // Options for rows per page
				rowsPerPage={table.state.pagination.pageSize} // Current page size
				page={table.state.pagination.pageIndex} // Current page index (0-based)
				onPageChange={(event, newPage) => {
					table.setPageIndex(newPage); // Set the new page index
				}}
				onRowsPerPageChange={(event) => {
					table.setPageSize(Number(event.target.value)); // Set the new page size
				}}
				labelRowsPerPage={t("rows_per_page")}
				labelDisplayedRows={({ from, to, count }) =>
					`${from}–${to} ${t("of")} ${
						count !== -1 ? count : `${t("more_than")} ${to}`
					}`
				}
			/>
		</>
	);
};

const TableView = ({ table, columns }) => {
	const { t } = useTranslation();

	return (
		<Table
			sx={{
				"@media screen and (max-width: 960px)": {
					display: "none",
				},
			}}
		>
			<TableHead>
				{table.getHeaderGroups().map((headerGroup) => (
					<TableRow key={`table-row-${headerGroup.id}`}>
						{headerGroup.headers.map((header) => {
							return (
								<TableCell
									key={`table-th-${header.id}`}
									component="th"
									sx={{
										padding: "3px 12px",
										...headerStyles({ id: header.id }),
									}}
								>
									{header.isPlaceholder
										? null
										: flexRender(
												header.column.columnDef.header,
												header.getContext(),
											)}
								</TableCell>
							);
						})}
					</TableRow>
				))}
			</TableHead>
			<TableBody>
				{table.getRowModel().rows?.length ? (
					table.getRowModel().rows.map((row) => {
						const providerCompanyName = row.original.providerCompanyName;
						const providerName = row.original.providerName;
						const providerPhone = row.original.providerPhone;
						const providerEmail = row.original.providerEmail;
						const publicNote = row.original.publicNote;

						return (
							<>
								<TableRow
									key={`row-${row.id}`}
									sx={{
										position: "relative",
									}}
								>
									{row.getVisibleCells().map((cell, index) => {
										return (
											<TableCell
												key={`cell-${cell.id}`}
												sx={{
													padding:
														row.original.publicNote || row.original.privateNote
															? "26px 12px"
															: "20px 12px",
													...cellsStyles({ id: cell.column.id }),
												}}
											>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
												{index === 0 && (
													<Box
														sx={{
															position: "absolute",
															maxWidth: "800px",
															width: "100%",
															bottom: "4px",
															left: 0,
															display: "flex",
															alignItems: "center",
															gap: "6px",
															color: "#A6A6A6",
															fontSize: "11px",
															ml: "12px",
															whiteSpace: "nowrap",
														}}
													>
														<Box
															sx={{
																background: "#F07C00",
																width: "5px",
																height: "5px",
																flex: "0 0 5px",
															}}
														></Box>
														{providerCompanyName && (
															<span>{providerCompanyName}</span>
														)}
														{providerName && <span>{providerName}</span>}
														{providerPhone && (
															<a href={`tel:${providerPhone.trim()}`}>
																{providerPhone}
															</a>
														)}
														{providerEmail && (
															<a href={`mailto:${providerEmail}`}>
																{providerEmail}
															</a>
														)}

														{publicNote && (
															<>
																<Box
																	sx={{
																		background: "#63c29a",
																		width: "5px",
																		height: "5px",
																		flex: "0 0 5px",
																	}}
																></Box>
																<span style={{ whiteSpace: "nowrap" }}>
																	{publicNote}
																</span>
															</>
														)}
													</Box>
												)}
											</TableCell>
										);
									})}
								</TableRow>
							</>
						);
					})
				) : (
					<TableRow>
						<TableCell colSpan={columns.length}>
							<Box
								sx={{
									border: "1px solid orange",
									padding: "30px",
									maxWidth: "600px",
									margin: "0 auto",
									marginTop: "30px",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
								}}
							>
								<Typography
									sx={{
										fontSize: "13px",
										color: "#a6a6a6",
									}}
								>
									{t("no_cargos_available")}
								</Typography>
							</Box>
						</TableCell>
					</TableRow>
				)}
			</TableBody>
		</Table>
	);
};

const GridView = ({ table }) => {
	const { t } = useTranslation();
	const [openRowId, setOpenRowId] = useState(null);

	const handleToggleGridAdditionalInfo = (rowId) => {
		setOpenRowId((prev) => (prev === rowId ? null : rowId));
	};

	return (
		<Box
			sx={{
				"@media screen and (max-width: 960px)": {
					display: "flex",
					// flexDirection: "column",
					flexWrap: "wrap",
					gap: "20px",
				},
				display: "none",
			}}
		>
			{table.getRowModel().rows?.length ? (
				table.getRowModel().rows.map((row) => {
					const providerCompanyName = row.original.providerCompanyName;
					const providerName = row.original.providerName;
					const providerPhone = row.original.providerPhone;
					const providerEmail = row.original.providerEmail;
					const publicNote = row.original.publicNote;

					const cells = row.getVisibleCells();
					const isRowOpen = openRowId === row.original.id;

					return (
						<Box
							key={row.original.id}
							sx={{
								border: (theme) => `2px solid ${theme.palette.barColor}`,
								borderRadius: "8px",
								flex: "1 0 calc(50% - 10px)",
								minWidth: "276px",
								padding: "10px",
								display: "flex",
								flexDirection: "column",
								position: "relative",
								overflow: "hidden",
							}}
						>
							<Box
								sx={{
									position: "absolute",
									zIndex: "10",
									right: isRowOpen ? "0" : "-2px",
									top: isRowOpen ? "0" : "-2px",
									width: isRowOpen ? "100%" : "auto",
									height: isRowOpen ? "100%" : "auto",
									background: (theme) =>
										isRowOpen ? theme.palette.barColor : "transparent",
									transition: "0.3s all",
								}}
							>
								<Box sx={{ display: "flex", justifyContent: "end" }}>
									<Tooltip
										title={
											<Typography
												variant="p"
												sx={{ fontSize: "9pt" }}
												component="span"
											>
												{t("commissioning_person_info")}
											</Typography>
										}
										placement="top"
									>
										<IconButton
											color="primary"
											onClick={() =>
												handleToggleGridAdditionalInfo(row.original.id)
											}
											sx={{
												border: (theme) =>
													`2px solid ${theme.palette.barColor}`,
												borderRadius: "8px",
												background: "#fff",
												"&:hover": {
													background: "#fff",
												},
											}}
										>
											<InfoIcon />
										</IconButton>
									</Tooltip>
								</Box>
								{isRowOpen && (
									<Box
										sx={{
											p: "10px",
											width: "100%",
											color: "#fff",
										}}
									>
										{providerCompanyName && (
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: "6px",
												}}
											>
												<Box
													sx={{
														background: "#F07C00",
														width: "5px",
														height: "5px",
														flex: "0 0 5px",
													}}
												></Box>
												<span>{providerCompanyName}</span>
											</Box>
										)}
										{providerName && (
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: "6px",
												}}
											>
												<Box
													sx={{
														background: "#F07C00",
														width: "5px",
														height: "5px",
														flex: "0 0 5px",
													}}
												></Box>
												<span>{providerName}</span>
											</Box>
										)}
										{providerPhone && (
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: "6px",
												}}
											>
												<Box
													sx={{
														background: "#F07C00",
														width: "5px",
														height: "5px",
														flex: "0 0 5px",
													}}
												></Box>
												<a
													style={{ color: "#fff" }}
													href={`tel:${providerPhone.trim()}`}
												>
													{providerPhone}
												</a>
											</Box>
										)}
										{providerEmail && (
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: "6px",
												}}
											>
												<Box
													sx={{
														background: "#F07C00",
														width: "5px",
														height: "5px",
														flex: "0 0 5px",
													}}
												></Box>
												<a
													style={{ color: "#fff" }}
													href={`mailto:${providerEmail}`}
												>
													{providerEmail}
												</a>
											</Box>
										)}
										{publicNote && (
											<Box
												sx={{
													display: "flex",
													alignItems: "baseline",
													gap: "6px",
												}}
											>
												<Box
													sx={{
														background: "#63c29a",
														width: "5px",
														height: "5px",
														flex: "0 0 5px",
													}}
												></Box>
												<span>{publicNote}</span>
											</Box>
										)}
									</Box>
								)}
							</Box>

							{table.getHeaderGroups().map((headerGroup) => (
								<Fragment key={`grid-item-${headerGroup.id}`}>
									{headerGroup.headers.map((header, index) => {
										const cell = cells[index];

										return (
											<Box
												key={`grid-box-${header.id}-${headerGroup.id}`}
												sx={{
													display: GRID_INDEXES_FOR_FLEX.includes(index)
														? "flex"
														: "block",
													borderTop: "1px solid #ccc",
													borderLeft: "1px solid #ccc",
													borderRight: "1px solid #ccc",
													"&:last-of-type": {
														borderBottom: "1px solid #ccc",
													},
												}}
											>
												<Box
													sx={{
														...headerStyles({ id: header.id }),
														padding: "3px 12px",
														flex: GRID_INDEXES_FOR_FLEX.includes(index)
															? "1 0 35%"
															: "1 0 100%",
														textAlign: "left",
														display: "flex",
														alignItems: "center",
													}}
												>
													{header.isPlaceholder
														? null
														: flexRender(
																header.column.columnDef.header,
																header.getContext(),
															)}
												</Box>
												<Box
													sx={{
														flex: GRID_INDEXES_FOR_FLEX.includes(index)
															? "1 0 65%"
															: "1 0 100%",
														p: "6px",
													}}
												>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext(),
													)}
												</Box>
											</Box>
										);
									})}
								</Fragment>
							))}
						</Box>
					);
				})
			) : (
				<Box>
					<Box
						sx={{
							border: "1px solid orange",
							padding: "30px",
							maxWidth: "600px",
							margin: "0 auto",
							marginTop: "30px",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
						}}
					>
						<Typography
							sx={{
								fontSize: "13px",
								color: "#a6a6a6",
							}}
						>
							{t("no_cargos_available")}
						</Typography>
					</Box>
				</Box>
			)}
		</Box>
	);
};
